(ns ui.view.transaction-process.components.file-upload
  "Shared file-upload building blocks for the transaction-process cards and
  sub-entity modals.

  The two flows differ in *when* files reach the backend:
  - Stage-process cards upload immediately (the card's slot already targets an
    existing process), tracking in-flight uploads via `uploads-in-progress`.
  - Sub-entity create modals stage raw `File` objects until the entity is
    created on save, then upload them. Those staged files are shown via
    `staged-files` and, per design, look like ordinary attachments — the user
    doesn't need to know the upload is deferred.

  All callers share the same `drop-zone` (drag/drop + click-to-browse) and
  `attachments-list` (already-uploaded attachments)."
  (:require
   [common.localization :refer [tr]]
   [clojure.string :as string]
   [oops.core :as oops]
   [reagent.core :as r]
   [ui.components.button.view :as button]
   [ui.util :as uu]
   [ui.view.transaction-process.components.attachment-row :as attachment-row]))

(defn- file-icon-class
  [content-type]
  (let [image? (some-> content-type (string/starts-with? "image"))
        pdf?   (= content-type "application/pdf")]
    (cond image? "fal fa-image"
          pdf?   "fal fa-file-pdf"
          :else  "fal fa-file")))

(defn- file-list->vec
  "Coerce a (array-like) DOM `FileList` into a vector of `File` objects."
  [file-list]
  (let [n (.-length file-list)]
    (mapv #(aget file-list %) (range n))))

(defn drop-zone
  "Dashed drag-and-drop + click-to-browse area. Calls `on-files` with a vector
  of JS `File` objects, already truncated to one when `multiple?` is false.

  Props:
  - `:on-files`  (fn [files]) — required.
  - `:multiple?` allow selecting/dropping more than one file.
  - `:disabled?` render inert (no drag/drop/click).
  - `:label`     override the prompt text (defaults by `multiple?`)."
  [_props]
  (let [dragging? (r/atom false)]
    (fn [{:keys [on-files multiple? disabled? label]}]
      (let [accept (fn [file-list]
                     (let [files (cond-> (file-list->vec file-list)
                                   (not multiple?) (->> (take 1) vec))]
                       (when (seq files)
                         (on-files files))))]
        [:div {:class         (uu/cm "border border-dashed min-h-24 rounded p-3 flex items-center justify-center transition-colors"
                                     (cond
                                       disabled?  "border-gray-200 opacity-50 cursor-not-allowed"
                                       @dragging? "border-primary-regular bg-primary-regular/5 cursor-pointer"
                                       :else      "border-gray-300 cursor-pointer hover:bg-gray-50"))
               :on-drag-over  (fn [e]
                                (.preventDefault e)
                                (when-not disabled? (reset! dragging? true)))
               :on-drag-leave (fn [_] (reset! dragging? false))
               :on-drop       (fn [e]
                                (.preventDefault e)
                                (reset! dragging? false)
                                (when-not disabled?
                                  (accept (.. e -dataTransfer -files))))
               :on-click      (fn []
                                (when-not disabled?
                                  (let [input (.createElement js/document "input")]
                                    (set! (.-type input) "file")
                                    (set! (.-multiple input) (boolean multiple?))
                                    (.addEventListener input "change"
                                                       (fn [e] (accept (.. e -target -files))))
                                    (.click input))))}
         [:div {:class "flex items-center gap-2 pointer-events-none"}
          [:i {:class "fal fa-cloud-upload text-gray-400"}]
          [:span {:class "text-sm m-0 text-gray-400"}
           (or label
               (if multiple?
                 (tr :drop-files-upload)
                 (tr :drop-file-upload)))]]]))))

(defn- file-row
  "A single file line. `:uploading?` swaps the icon for a spinner; `:on-remove`
  (when given) renders a hover X. Styled to match `attachment-row/view` so
  staged files are visually indistinguishable from uploaded ones."
  [{:keys [filename content-type uploading? on-remove]}]
  [:div {:class "group/file flex items-center gap-3 py-1.5 px-2 rounded hover:bg-gray-50"}
   [:div {:class "flex items-center gap-2 text-sm m-0 text-gray-700"}
    [:i {:class (uu/cm "text-gray-400"
                       (if uploading?
                         "fal fa-spinner-third fa-spin"
                         (file-icon-class content-type)))}]
    [:span filename]]
   (when on-remove
     [:div {:class "opacity-0 group-hover/file:opacity-100"}
      [button/square-secondary-open
       {:tooltip  (tr :remove)
        :on-click on-remove}
       [:i {:class "fal fa-times"}]]])])

(defn attachments-list
  "Renders already-uploaded attachments. `:attachments` is a seq of attachment
  entities; `:target-id` is the entity they hang off (for delete)."
  [{:keys [attachments target-id read-only?]}]
  (when (seq attachments)
    [:div {:class "flex flex-col mb-2"}
     (doall
      (for [att attachments]
        ^{:key (:db/id att)}
        [attachment-row/view {:attachment att
                              :target-id  target-id
                              :read-only? read-only?}]))]))

(defn uploads-in-progress
  "In-flight uploads for the immediate-upload (stage card) flow. `:files` is a
  seq of `{:filename ...}`."
  [{:keys [files]}]
  (when (seq files)
    [:div {:class "flex flex-col gap-1 mt-2"}
     (doall
      (for [{:keys [filename]} files]
        ^{:key filename}
        [file-row {:filename filename :uploading? true}]))]))

(defn staged-files
  "Files staged for deferred upload (sub-entity create modal). Rendered like
  ordinary attachments. `:files` is a vector of JS `File` objects; `:on-remove`
  is (fn [idx])."
  [{:keys [files on-remove]}]
  (when (seq files)
    [:div {:class "flex flex-col mb-2"}
     (doall
      (for [[idx file] (map-indexed vector files)]
        ^{:key idx}
        [file-row {:filename     (oops/oget file "name")
                   :content-type (oops/oget file "type")
                   :on-remove    (when on-remove #(on-remove idx))}]))]))
